import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiTrash2, FiMinus, FiPlus, FiShoppingBag, FiArrowLeft, FiZap } from 'react-icons/fi';
import { useCart } from '../context/CartContext';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const BackButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #666;
  text-decoration: none;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: #f5f5f5;
    color: #1a1a1a;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: #1a1a1a;
`;

const CartContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const CartItems = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const CartItem = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr auto;
  gap: 1rem;
  padding: 1.5rem 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    grid-template-columns: 80px 1fr;
    gap: 1rem;
  }
`;

const ItemImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 8px;

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
`;

const ImagePlaceholder = styled.div`
  width: 100px;
  height: 100px;
  background: #f5f5f5;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 12px;

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ItemTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  line-height: 1.3;
`;

const ItemBrand = styled.p`
  color: #666;
  font-size: 14px;
  margin-bottom: 0.5rem;
`;

const ItemSize = styled.span`
  display: block;
  width: fit-content;
  margin-top: 0.35rem;
  padding: 0.22rem 0.5rem;
  border-radius: 999px;
  background: #f0edff;
  color: #5544bf;
  font-size: 0.72rem;
  font-weight: 700;
`;

const ItemPrice = styled.p`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a1a1a;
`;

const ItemActions = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;

  @media (max-width: 768px) {
    grid-column: 1 / -1;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-top: 1rem;
  }
`;

const QuantityContainer = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;

const QuantityButton = styled.button`
  width: 35px;
  height: 35px;
  border: none;
  background: #f5f5f5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;

  &:hover {
    background: #e0e0e0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityDisplay = styled.span`
  width: 50px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  background: white;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #e74c3c;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:hover {
    background: #fee;
  }
`;

const CartSummary = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: fit-content;
  position: sticky;
  top: 2rem;
`;

const SummaryTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: bold;
  color: #1a1a1a;
  margin-bottom: 1.5rem;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f0;

  &:last-of-type {
    border-bottom: 2px solid #1a1a1a;
    font-weight: bold;
    font-size: 1.2rem;
  }
`;

const CheckoutButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: #1a1a1a;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1.5rem;

  &:hover {
    background: #333;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const AIComboButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);

  &:hover {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const EmptyCartIcon = styled.div`
  font-size: 4rem;
  color: #ccc;
  margin-bottom: 1rem;
`;

const EmptyCartTitle = styled.h2`
  font-size: 1.5rem;
  color: #1a1a1a;
  margin-bottom: 1rem;
`;

const EmptyCartText = styled.p`
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const ShopButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #1a1a1a;
  color: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: #333;
    transform: translateY(-1px);
  }
`;

const CartPage: React.FC = () => {
  const { cart, updateQuantity, removeFromCart } = useCart();

  const handleQuantityChange = (productId: string, selectedSize: string | undefined, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeFromCart(productId, selectedSize);
    } else {
      updateQuantity(productId, newQuantity, selectedSize);
    }
  };

  const formatPrice = (price: number, currency = 'TRY') => {
    return `${price.toLocaleString('tr-TR')} ${currency}`;
  };

  const handleAICombo = () => {
    // Sadece ürün ID'lerini ve miktarlarını gönder
    const productIds = cart.items.map(item => ({
      productId: item.product.sku || item.product.id,
      quantity: item.quantity,
      selectedSize: item.selectedSize,
    }));

    // Open AI combination widget with simplified data
    if (window.FashionConnect) {
      window.FashionConnect.openBasketCombination({
        items: productIds
      } as any);
    } else {
      alert('AI Kombin özelliği şu anda kullanılamıyor. Lütfen sayfayı yenileyin.');
    }
  };

  if (cart.items.length === 0) {
    return (
      <Container>
        <Header>
          <BackButton to="/products">
            <FiArrowLeft />
            Alışverişe Devam Et
          </BackButton>
          <Title>Sepetim</Title>
        </Header>

        <EmptyCart>
          <EmptyCartIcon>
            <FiShoppingBag />
          </EmptyCartIcon>
          <EmptyCartTitle>Sepetiniz Boş</EmptyCartTitle>
          <EmptyCartText>
            Henüz sepetinize ürün eklemediniz. 
            Harika ürünlerimizi keşfetmek için alışverişe başlayın!
          </EmptyCartText>
          <ShopButton to="/products">
            <FiShoppingBag />
            Alışverişe Başla
          </ShopButton>
        </EmptyCart>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton to="/products">
          <FiArrowLeft />
          Alışverişe Devam Et
        </BackButton>
        <Title>Sepetim ({cart.itemCount} ürün)</Title>
      </Header>

      <CartContainer>
        <CartItems>
          {cart.items.map((item) => (
            <CartItem key={`${item.product.id}:${item.selectedSize || 'default'}`}>
              {item.product.images && item.product.images.length > 0 ? (
                <ItemImage 
                  src={item.product.images[0]} 
                  alt={item.product.title} 
                />
              ) : (
                <ImagePlaceholder>
                  Resim Yok
                </ImagePlaceholder>
              )}

              <ItemInfo>
                <div>
                  {item.product.brand && (
                    <ItemBrand>{item.product.brand.name}</ItemBrand>
                  )}
                  <ItemTitle>{item.product.title}</ItemTitle>
                  {item.selectedSize && <ItemSize>Beden {item.selectedSize}</ItemSize>}
                </div>
                <ItemPrice>
                  {item.product.price 
                    ? formatPrice(item.product.price, item.product.currency)
                    : 'Fiyat Belirtilmemiş'
                  }
                </ItemPrice>
              </ItemInfo>

              <ItemActions>
                <QuantityContainer>
                  <QuantityButton
                    onClick={() => handleQuantityChange(item.product.id, item.selectedSize, item.quantity - 1)}
                  >
                    <FiMinus size={14} />
                  </QuantityButton>
                  <QuantityDisplay>{item.quantity}</QuantityDisplay>
                  <QuantityButton
                    onClick={() => handleQuantityChange(item.product.id, item.selectedSize, item.quantity + 1)}
                  >
                    <FiPlus size={14} />
                  </QuantityButton>
                </QuantityContainer>

                <RemoveButton
                  onClick={() => removeFromCart(item.product.id, item.selectedSize)}
                  title="Sepetten Kaldır"
                >
                  <FiTrash2 size={18} />
                </RemoveButton>
              </ItemActions>
            </CartItem>
          ))}
        </CartItems>

        <CartSummary>
          <SummaryTitle>Sipariş Özeti</SummaryTitle>
          
          <SummaryRow>
            <span>Ürün Sayısı:</span>
            <span>{cart.itemCount} adet</span>
          </SummaryRow>

          <SummaryRow>
            <span>Ara Toplam:</span>
            <span>{formatPrice(cart.total)}</span>
          </SummaryRow>

          <SummaryRow>
            <span>Kargo:</span>
            <span>{cart.total >= 500 ? 'Ücretsiz' : formatPrice(29.90)}</span>
          </SummaryRow>

          <SummaryRow>
            <span>Toplam:</span>
            <span>{formatPrice(cart.total + (cart.total >= 500 ? 0 : 29.90))}</span>
          </SummaryRow>

          <CheckoutButton>
            Siparişi Tamamla
          </CheckoutButton>

          {cart.items.length >= 2 && (
            <AIComboButton onClick={handleAICombo}>
              <FiZap size={18} />
              AI Kombin Oluştur
            </AIComboButton>
          )}

          {cart.total < 500 && (
            <p style={{ 
              fontSize: '14px', 
              color: '#666', 
              textAlign: 'center', 
              marginTop: '1rem' 
            }}>
              {formatPrice(500 - cart.total)} daha alışveriş yapın, kargo ücretsiz olsun!
            </p>
          )}
        </CartSummary>
      </CartContainer>
    </Container>
  );
};

export default CartPage;
